<template>
    <div class="online-page" v-reveal="{ anim: 'unfold', stagger: 90, y: 28 }">
        <!-- 顶部统计 -->
        <section class="stats-grid" v-reveal="{ anim: 'pop', stagger: 80, y: 22 }">
            <StatCard label="总会话" :value="items.length" icon="🖥" accent="violet" hint="聚合后的会话条目" />
            <StatCard label="当前在线" :value="onlineCount" icon="📡" accent="mint" hint="在线判定内活跃" />
            <StatCard label="多开实例" :value="multiCount" icon="⚡" accent="amber" hint="同卡密多会话" />
            <StatCard label="显示中" :value="filtered.length" icon="🔎" accent="cyan" hint="符合筛选条件" />
        </section>

        <!-- 工具栏 -->
        <section class="toolbar card neon">
            <div class="tb-left">
                <div class="field-inline">
                    <span class="cap nowrap">在线判定秒数</span>
                    <input class="input mini-num" v-model.number="within" type="number" min="10" step="10"
                        @change="load()" />
                </div>
                <div class="search-box">
                    <span class="search-ico">🔍</span>
                    <input class="input search" v-model.trim="q" placeholder="搜索卡密或 HWID…" />
                </div>
            </div>
            <div class="tb-right">
                <label class="check"><input type="checkbox" v-model="onlyOnline" /><span>只看在线</span></label>
                <label class="check"><input type="checkbox" v-model="onlyMulti" /><span>只看多开</span></label>
                <div class="view-switch">
                    <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="卡片视图">▦</button>
                    <button :class="{ on: view === 'table' }" @click="view = 'table'" title="表格视图">≣</button>
                </div>
                <button class="btn" @click="resetFilters">重置</button>
                <button class="btn btn-primary" @click="load()">刷新</button>
            </div>
        </section>

        <!-- 卡片视图 -->
        <div v-if="view === 'grid'" class="cards">
            <TiltCard v-for="(it, i) in filtered" :key="it.license_key + ':' + it.hwid_hash" :max="7"
                :disabled="isTouch" class="sess-card">
                <div class="card-in" :style="{ animationDelay: Math.min(i * 0.045, 0.7) + 's' }">
                    <div class="sc-top">
                        <span class="pill" :class="it.online ? 'pill-good' : 'pill-ok'">
                            <span class="dot" :class="{ live: it.online }"></span>
                            {{ it.online ? '在线' : '离线' }}
                        </span>
                        <span class="pill" :class="Number(it.active_sessions || 0) > 1 ? 'pill-warn' : 'pill-ok'">
                            {{ Number(it.active_sessions || 0) }} 开
                        </span>
                    </div>

                    <div class="sc-key mono" :title="it.license_key">{{ it.license_key }}</div>

                    <div class="sc-row">
                        <span class="sc-label muted2">设备 HWID</span>
                        <span class="sc-val mono ellipsis" :title="it.hwid_hash">{{ it.hwid_hash || '-' }}</span>
                    </div>
                    <div class="sc-row">
                        <span class="sc-label muted2">最后心跳</span>
                        <span class="sc-val">{{ fmtTime(it.last_seen_at || it.last_heartbeat_at) }}</span>
                    </div>
                    <div class="sc-row" v-if="it.online_minutes != null">
                        <span class="sc-label muted2">活跃</span>
                        <span class="sc-val">{{ it.online_minutes }} 分钟前</span>
                    </div>
                    <div class="sc-row">
                        <span class="sc-label muted2">加载器指纹</span>
                        <span class="sc-val mono ellipsis" :title="it.loader_hash || '-'">{{ it.loader_hash || '-' }}</span>
                    </div>
                </div>
            </TiltCard>
        </div>

        <!-- 表格视图 -->
        <div v-else class="table-card card neon">
            <div class="table-scroll">
                <table class="table">
                    <thead>
                        <tr>
                            <th>卡密</th>
                            <th>设备 HWID</th>
                            <th>状态</th>
                            <th>实例数</th>
                            <th>最后心跳</th>
                            <th>加载器指纹</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="it in filtered" :key="it.license_key + ':' + it.hwid_hash" class="row-hover">
                            <td class="mono"><div class="ellipsis" :title="it.license_key">{{ it.license_key }}</div></td>
                            <td class="mono muted"><div class="ellipsis" :title="it.hwid_hash">{{ it.hwid_hash }}</div></td>
                            <td><span class="pill" :class="it.online ? 'pill-good' : 'pill-ok'">{{ it.online ? '在线' : '离线' }}</span></td>
                            <td :class="Number(it.active_sessions || 0) > 1 ? 'warn' : 'muted'">{{ Number(it.active_sessions || 0) }} 开</td>
                            <td>
                                <div>{{ fmtTime(it.last_seen_at || it.last_heartbeat_at) }}</div>
                                <div class="small muted2" v-if="it.online_minutes != null">{{ it.online_minutes }} 分钟前</div>
                            </td>
                            <td class="mono muted"><div class="ellipsis" :title="it.loader_hash || '-'">{{ it.loader_hash || '-' }}</div></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <EmptyState v-if="!filtered.length" title="暂无在线数据" desc="可调大在线判定秒数，或关闭筛选条件后再试。" />
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from "vue";
import { onlineApi } from "../api/online";
import StatCard from "../components/StatCard.vue";
import TiltCard from "../components/TiltCard.vue";
import EmptyState from "../components/EmptyState.vue";

const inst = getCurrentInstance();
function notify(type, title, message) {
    inst?.root?.proxy?.$toast?.push?.({ type, title, message });
}

const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

const within = ref(180);
const q = ref("");
const onlyOnline = ref(false);
const onlyMulti = ref(false);
const view = ref("grid");
const items = ref([]);
let timer = null;

function fmtTime(v) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const filtered = computed(() => {
    const kw = String(q.value || "").trim().toLowerCase();
    return (items.value || []).filter((it) => {
        const hitKw =
            !kw ||
            String(it.license_key || "").toLowerCase().includes(kw) ||
            String(it.hwid_hash || "").toLowerCase().includes(kw);
        const hitOnline = !onlyOnline.value || it.online === true;
        const hitMulti = !onlyMulti.value || Number(it.active_sessions || 0) > 1;
        return hitKw && hitOnline && hitMulti;
    });
});

const onlineCount = computed(() => (items.value || []).filter((x) => x.online === true).length);
const multiCount = computed(() => (items.value || []).filter((x) => Number(x.active_sessions || 0) > 1).length);

// silent=true 时不弹提示，用于后台轮询
async function load(silent = false) {
    try {
        const out = await onlineApi.list({ within_seconds: within.value });
        if (!out?.ok) {
            notify("bad", "在线列表加载失败", out?.code || "FAILED");
            return;
        }
        items.value = (out.items || out.rows || []).map((it) => ({
            ...it,
            active_sessions: Number(it.active_sessions || 0),
        }));
        if (!silent) notify("ok", "已刷新", `共 ${items.value.length} 条`);
    } catch (e) {
        if (!silent) notify("bad", "网络错误", String(e?.message || e));
    }
}

function resetFilters() {
    q.value = "";
    onlyOnline.value = false;
    onlyMulti.value = false;
    within.value = 180;
    load();
}

onMounted(() => {
    load(true);
    timer = setInterval(() => load(true), 10000); // 与旧逻辑一致：10 秒轮询
});

onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
});
</script>

<style scoped>
.online-page {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    flex-wrap: wrap;
}

.tb-left,
.tb-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cap {
    margin-bottom: 0;
}

.nowrap {
    white-space: nowrap;
}

.mini-num {
    width: 100px;
}

.search-box {
    position: relative;
}

.search-ico {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    opacity: .6;
    font-size: 13px;
}

.search {
    width: 240px;
    max-width: 100%;
    padding-left: 34px;
}

.check {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--muted);
    cursor: pointer;
    user-select: none;
}

.view-switch {
    display: inline-flex;
    border: 1px solid var(--stroke);
    border-radius: 10px;
    overflow: hidden;
}

.view-switch button {
    width: 38px;
    height: 38px;
    background: transparent;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 16px;
}

.view-switch button.on {
    background: var(--grad-violet-cyan);
    color: #fff;
}

.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
}

.sess-card {
    padding: 16px;
}

.sc-top {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.sc-key {
    font-size: 14px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 12px;
}

.sc-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border-top: 1px solid var(--stroke-soft);
    font-size: 13px;
}

.sc-label {
    flex: 0 0 auto;
    font-size: 12px;
}

.sc-val {
    min-width: 0;
    text-align: right;
}

.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
}

.table-card {
    padding: 6px;
}

.table-scroll {
    overflow: auto;
    border-radius: var(--r-md);
    max-height: 72vh;
}

.small {
    font-size: 12px;
}

@media (max-width: 1100px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .tb-left,
    .tb-right {
        width: 100%;
        justify-content: space-between;
    }

    .search {
        width: 100%;
    }

    .cards {
        grid-template-columns: 1fr;
    }
}
</style>
